/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 17/04/2025 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, CardContent, Typography, Divider, Button, Tabs, Tab, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch, Chip, MenuItem } from "@mui/material";

import { useParametros } from "../../context/Parametros/ParametrosContext";
import { useSesion } from "../../context/System/SesionContext";
import { useRequest } from "../../context/System/RequestContext";
import { apiParametrosGet, apiParametrosSave } from "../../settings/ApiConfig";
import MnuParametrosCajaActions from "./MnuParametrosCajaActions";
import TicketDetalleDesigner from "./TicketDetalleDesigner";
import TicketPreview from "./TicketPreview";
import { concatenarPartes } from "../../settings/ticketEscPos";
import { buildTicketVentaParts } from "../../settings/ticketVentaEscape";
import { imprimirTicketEnPrinterTools, parseEscPosPreview } from "../../settings/ticketImpresion";
import { construirDatosTicketMuestra } from "../../settings/ticketMuestra";
import {
    anchoPapelPx,
    tamanoFuentePreviewCaracter,
    multiplicadorFuenteTipo,
    altoLineaMm,
    anchoUtilMm,
    anchoCaracterMm,
    CALIBRACION_POR_DEFECTO,
} from "../../settings/ticketPerfil";
import {
  normalizarObjetoDetalle,
  CAMPOS_DATOS,
  CAMPOS_DETALLE,
  CAMPOS_TOTALES,
  LABELS_DATOS,
  LABELS_DETALLE,
  LABELS_TOTALES,
} from "../../settings/ticketLayoutCatalogs";
import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm";
import { useFormTabLock } from "../../hooks/useFormTabLock";
import { useLayout } from "../../context/System/LayoutContext";

const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Ancho del ticket (papel imprimible) y columnas por linea.
// 58 mm -> 48 mm imprimibles -> 32 columnas (impresoras de produccion)
// 80 mm -> 72 mm imprimibles -> 60 columnas
const ANCHO_TICKET_MM = 48;
const COLUMNAS_TICKET = 32;

// La UI trabaja en CENTIMETROS; el guardado sigue en mm hasta la migracion
// del layout a dots.
const mmToCm = (mm) => Math.round(((Number(mm) || 0) / 10) * 100) / 100;
const cmToMm = (cm) => Math.round((Number(cm) || 0) * 10 * 100) / 100;

const defaultTicketLayout = {
  columnas: COLUMNAS_TICKET,
  logo: { ancho: 50, alto: 0 },
  cabecero: { ancho: ANCHO_TICKET_MM, alto: 50, negrita: false, alineacion: "center", fuenteTipo: "A" },
  datos: { objetos: [] },
  detalle: { objetos: [] },
  totales: { objetos: [] },
  pie: { ancho: ANCHO_TICKET_MM, alto: 50, negrita: false, alineacion: "center", fuenteTipo: "A" },
};

const normalizarSeccionTicket = (seccion, campos) => ({
  ...(seccion || {}),
  objetos: (seccion?.objetos || []).map((o, i) => normalizarObjetoDetalle(o, i, campos)),
});

const cloneConfigNoFolios = (target, source) => {
  const S = (field, fallback) => (source?.[field] !== undefined && source?.[field] !== null) ? source[field] : fallback;
  
  return {
    ...target,
    // General clonable
    dominio: S("dominio", target?.dominio),

    // Ticket clonable
    encabezadoticket: S("encabezadoticket", target?.encabezadoticket),
    pieticket: S("pieticket", target?.pieticket),
    logo: S("logo", target?.logo),

    // Impresoras clonables
    nombreimpresoraventas: S("nombreimpresoraventas", target?.nombreimpresoraventas),
    nombreimpresorapreventas: S("nombreimpresorapreventas", target?.nombreimpresorapreventas),
    nombreimpresoracotizaciones: S("nombreimpresoracotizaciones", target?.nombreimpresoracotizaciones),
    nombreimpresoraapartados: S("nombreimpresoraapartados", target?.nombreimpresoraapartados),
    nombreimpresorareportes: S("nombreimpresorareportes", target?.nombreimpresorareportes),
    nombreimpresorabanner: S("nombreimpresorabanner", target?.nombreimpresorabanner),
    nombreimpresoraetiqueta: S("nombreimpresoraetiqueta", target?.nombreimpresoraetiqueta),
    nombreimpresoracodigobarras: S("nombreimpresoracodigobarras", target?.nombreimpresoracodigobarras),

    // Flags clonables
    statusviewclient: S("statusviewclient", target?.statusviewclient),
    locationviewclient: S("locationviewclient", target?.locationviewclient),
    sizeviewclient: S("sizeviewclient", target?.sizeviewclient),
  };
};

async function getLocalPrinters() {
  // El PrinterTools vigente ya no expone GET /api/impresoras (404); la lista
  // con estado vive en GET /api/impresoras/detalle -> [{deviceId, name, ...}]
  try {
    const r = await fetch("http://localhost:4000/api/impresoras/detalle");
    if (!r.ok) return { ok: false, printers: [], message: `PrinterTools respondió ${r.status}, sin lista de impresoras` };

    const contentType = (r.headers.get("content-type") || "").toLowerCase();
    if (!contentType.includes("json")) return { ok: false, printers: [], message: "PrinterTools respondió formato inesperado" };

    const data = await r.json();
    const printers = (Array.isArray(data) ? data : data?.result ?? [])
      .map((p) => (p?.name ?? "").toString().trim())
      .filter(Boolean);

    if (printers.length === 0) return { ok: false, printers: [], message: "PrinterTools no reportó impresoras instaladas" };
    return { ok: true, printers: [...new Set(printers)] };
  } catch {
    return { ok: false, printers: [], message: "PrinterTools no está disponible (localhost:4000)" };
  }
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

// Nombre de la computadora actual según PrinterTools (servicio local).
// Si el servicio no está disponible se resuelve "" y se sigue con selección manual.
async function obtenerNombreEquipoLocal(timeoutMs = 4000) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const r = await fetch("http://localhost:4000/api/nombreequipo", { signal: controller.signal });
    clearTimeout(timer);
    if (!r.ok) return "";
    const data = await r.json();
    const nombre = data?.name ?? data?.nombre ?? data?.equipo ?? "";
    return (nombre ?? "").toString().trim().toUpperCase();
  } catch {
    return "";
  }
}

// A nivel módulo (no dentro del formulario): si se declara adentro, cada tecla
// re-renderiza, React lo desmonta/remonta y se pierde el foco de edición.
const PrinterField = ({ label, field, draft, options, loading, listOk, setField }) => {
  const texto = (draft?.[field] ?? "").toString();
  return (
    <Autocomplete
      options={options}
      loading={loading}
      freeSolo
      value={texto}
      inputValue={texto}
      onChange={(_, v) => setField(field, (typeof v === "string" ? v : v ?? "").toString())}
      onInputChange={(_, v) => setField(field, (v ?? "").toString())}
      renderInput={(params) => (
        <TextField
          autoComplete="off"
          {...params}
          label={label}
          size="small"
          helperText={
            listOk
              ? "Selecciona o escribe el nombre exacto."
              : "Sin lista local de impresoras (puedes escribir manualmente)."
          }
        />
      )}
    />
  );
};

export default function ParametrosCaja() {
  const { isLocked: isFormLocked } = useFormTabLock('parametros_caja', {
    title: 'Parámetros de Caja',
    onLockDetected: (info) => {
        console.warn(`El formulario de ${info?.title || 'Parámetros de Caja'} ya está abierto en otra pestaña`);
    }
  });

  const {
    handleobtenerListaEquipos,
    listaEquipos,
    handleObtenerParametrosCaja,
    handleSaveParametrosCaja,
    normalizeCaja,
    handleClickRegresarMainDashboard,
  } = useParametros();

  const { handleOpenAlert } = useLayout();
    const { parametrosCajaForm, setParametrosCajaForm } = useSesion();
  const { GetRequest, PostRequest } = useRequest();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tag = event.target?.tagName?.toLowerCase();
      if (tag === "textarea" || tag === "input") return;

      if (event.key.trim().toUpperCase() === "F4") {
        event.preventDefault();
        event.stopPropagation();
        handleClickRegresarMainDashboard();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClickRegresarMainDashboard]);

  useEffect(() => {
    if (isFormLocked) {
      handleOpenAlert("El formulario de Parámetros de Caja ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.", "error");
      setTimeout(() => {
        if(handleClickRegresarMainDashboard) handleClickRegresarMainDashboard();
      }, 900);
    }
  }, [isFormLocked, handleClickRegresarMainDashboard, handleOpenAlert]);

  // UI state
  const [tab, setTab] = useState(0);
  const [loadingEquipos, setLoadingEquipos] = useState(true);

  const [loadingCaja, setLoadingCaja] = useState(false);
  const [saving, setSaving] = useState(false);

  const [server, setServer] = useState(null);
  const [draft, setDraft] = useState(null);

  const [selectedEquipo, setSelectedEquipo] = useState(null); // objeto de listaEquipos

  // impresoras
  const [printersLoading, setPrintersLoading] = useState(true);
  const [printersInfo, setPrintersInfo] = useState({ ok: false, printers: [], message: "" });

  // clonación
  const [openClone, setOpenClone] = useState(false);
  const [cloneFromEquipo, setCloneFromEquipo] = useState(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneKeepTicket, setCloneKeepTicket] = useState(true);
  const [cloneKeepPrinters, setCloneKeepPrinters] = useState(true);
  const [cloneKeepGeneral, setCloneKeepGeneral] = useState(true);
  const [cloneKeepFlags, setCloneKeepFlags] = useState(true);

  // Importación del esquema (solo diseño del ticket) desde otra caja.
  const [openImportarEsquema, setOpenImportarEsquema] = useState(false);
  const [importarDesdeEquipo, setImportarDesdeEquipo] = useState(null);
  const [importandoEsquema, setImportandoEsquema] = useState(false);

  const [localAlert, setLocalAlert] = useState(null); // {type, message}
  const [ticketLayout, setTicketLayout] = useState(defaultTicketLayout);
  const [seleccionObjeto, setSeleccionObjeto] = useState({ seccion: null, id: null });
  const [serverTicketLayout, setServerTicketLayout] = useState(defaultTicketLayout);
  const [previewTicket, setPreviewTicket] = useState(null); // {titulo, lineas}
  const [probandoTicket, setProbandoTicket] = useState(false);
  // El area imprimible REAL: columnas x ancho real del caracter. Es el limite
  // del papel (con 9 dots por caracter, 32 columnas son 3.6 cm, no 4.8).
  const calibracionEfectiva = { ...CALIBRACION_POR_DEFECTO, ...(ticketLayout?.calibracion || {}) };
  const columnasEfectivas = ticketLayout?.columnas ?? COLUMNAS_TICKET;
  const anchoUtilMmReal = anchoUtilMm(columnasEfectivas, calibracionEfectiva);
  const anchoCaracterMmReal = anchoCaracterMm(calibracionEfectiva);
  const anchoDotsTicket = Math.max(1, Math.round(
    anchoUtilMmReal * (calibracionEfectiva.dotsPorMm || CALIBRACION_POR_DEFECTO.dotsPorMm)
  ));

  // Ticket de prueba: arma el binario con DATOS FICTICIOS usando el mismo motor
  // de la impresion real, sin registrar ninguna venta.
  const construirParametrosPrueba = () => ({
    ...(parametrosCajaForm || {}),
    ticketLayout,
    encabezadoticket: draft?.encabezadoticket ?? "",
    pieticket: draft?.pieticket ?? "",
  });

  const prepararPartesTicketPrueba = () => {
    const parametros = construirParametrosPrueba();
    const datos = construirDatosTicketMuestra({
      caja: draft?.caja || parametros.caja || "CAJA-PRUEBA",
      encabezado: draft?.encabezadoticket || "TICKET DE PRUEBA",
    });
    const { partes } = buildTicketVentaParts({ dataTicket: datos, parametrosCajaForm: parametros });
    return { partes, parametros };
  };

  const handleVistaPreviaTicketPrueba = () => {
    try {
      const { partes } = prepararPartesTicketPrueba();
      setPreviewTicket({
        titulo: "Vista previa del ticket (datos de prueba)",
        lineas: parseEscPosPreview(concatenarPartes(partes), {
          columnasMax: columnasEfectivas,
          anchoCaracterDots: calibracionEfectiva.anchoCaracterDots,
        }),
      });
    } catch (error) {
      handleOpenAlert("No fue posible generar la vista previa: " + error.message, "error");
    }
  };

  const handleImprimirTicketPrueba = async () => {
    try {
      setProbandoTicket(true);
      const { partes, parametros } = prepararPartesTicketPrueba();
      await imprimirTicketEnPrinterTools({ partes, parametrosCajaForm: parametros });
      handleOpenAlert("Ticket de prueba enviado a la impresora", "success");
    } catch (error) {
      handleOpenAlert("Error al imprimir el ticket de prueba: " + error.message, "error");
    } finally {
      setProbandoTicket(false);
    }
  };

  const dirty = useMemo(() => {
    if (!server || !draft) return false;
    return !deepEqual(server, draft) || !deepEqual(serverTicketLayout, ticketLayout);
  }, [server, draft, serverTicketLayout, ticketLayout]);

  // Carga lista equipos y autocarga la caja de esta computadora vía
  // PrinterTools (/api/nombreequipo) para no buscarla manualmente.
  const autoDetectadoRef = useRef(false);
  useEffect(() => {
    const run = async () => {
      setLoadingEquipos(true);
      try {
        const equipos = await handleobtenerListaEquipos();
        if (autoDetectadoRef.current) return;
        autoDetectadoRef.current = true;
        const nombreLocal = await obtenerNombreEquipoLocal();
        if (!nombreLocal || !Array.isArray(equipos) || equipos.length === 0) return;
        const coincidencia = equipos.find((e) => {
          const candidatos = [e?.pc, e?.nombre, e?.caja, e?.descripcion];
          return candidatos.some((c) => (c ?? "").toString().trim().toUpperCase() === nombreLocal);
        });
        if (coincidencia && getEquipoId(coincidencia)) {
          setSelectedEquipo(coincidencia);
          await loadCaja(coincidencia);
          setLocalAlert({ type: "info", message: `Caja actual detectada (${getEquipoLabel(coincidencia)}), parámetros cargados. Puedes cambiar de caja si lo necesitas.` });
        }
      } finally {
        setLoadingEquipos(false);
      }
    };
    run();
  }, []);

  // Carga impresoras locales
  useEffect(() => {
    const run = async () => {
      setPrintersLoading(true);
      const info = await getLocalPrinters();
      setPrintersInfo(info);
      setPrintersLoading(false);
    };
    run();
  }, []);

  const getEquipoLabel = (e) => {
    if (!e) return "";
    if (typeof e === "string" || typeof e === "number") return String(e);
    return String(e.pc ?? e.nombre ?? e.caja ?? e.descripcion ?? e.idpc ?? "").trim();
  };

  const getEquipoId = (e) => {
    if (!e) return "";
    if (typeof e === "string" || typeof e === "number") return String(e).trim();
    return String(e.idpc ?? e.idPC ?? e.IdPc ?? e.guid ?? e.id ?? "").trim();
  };

  const loadCaja = async (equipoObj) => {
    
    const idCaja = getEquipoId(equipoObj);
    const label = getEquipoLabel(equipoObj);
    if (!idCaja) return;

    setLocalAlert(null);
    setLoadingCaja(true);
    try {
      const data = await handleObtenerParametrosCaja(idCaja);
      if (!data) {
        setServer(null);
        setDraft(null);
        setTicketLayout(defaultTicketLayout);
        setServerTicketLayout(defaultTicketLayout);
        return;
      }
      const norm = normalizeCaja ? normalizeCaja(data) : data;
      setServer(norm);
      setDraft(norm);
      setTab(0);

      // Cargar props de ticket para esta caja (tabla Parametros, clave TICKET_LAYOUT_{caja})
      const idCajaProps = getEquipoId(equipoObj);
      if (idCajaProps) {
        try {
          const nombreLayout = `TICKET_LAYOUT_${idCajaProps}`;
          const respProps = await GetRequest({
            url: apiParametrosGet.replace("{nombre}", nombreLayout),
            loader: "Consultando propiedades de ticket...",
          });

          if (!respProps.hasError && respProps.result != null) {
            const parsed = typeof respProps.result === "string" ? JSON.parse(respProps.result) : respProps.result;
            setTicketLayout(parsed || defaultTicketLayout);
            setServerTicketLayout(parsed || defaultTicketLayout);
          } else {
            setTicketLayout(defaultTicketLayout);
            setServerTicketLayout(defaultTicketLayout);
          }
        } catch (e) {
          setTicketLayout(defaultTicketLayout);
          setServerTicketLayout(defaultTicketLayout);
        }
      }
    } catch (err) {
      setLocalAlert({ type: "error", message: err?.message || "Error al cargar parámetros" });
    } finally {
      setLoadingCaja(false);
    }

  };

  const guardarPropsTicket = async () => {
    if (!selectedEquipo) return;
    const idCaja = getEquipoId(selectedEquipo);
    if (!idCaja) return;
    const layoutParaGuardar = {
      ...ticketLayout,
      datos: normalizarSeccionTicket(ticketLayout?.datos, CAMPOS_DATOS),
      detalle: normalizarSeccionTicket(ticketLayout?.detalle, CAMPOS_DETALLE),
      totales: normalizarSeccionTicket(ticketLayout?.totales, CAMPOS_TOTALES),
    };
    const resp = await PostRequest({
      url: apiParametrosSave,
      body: {
        nombre: `TICKET_LAYOUT_${idCaja}`,
        valor: JSON.stringify(layoutParaGuardar),
      },
      loader: "Guardando propiedades de ticket...",
    });

    if (resp?.hasError) {
      throw new Error(resp?.message || "No fue posible guardar las propiedades de ticket.");
    }
    await cargarPropsTicket(idCaja);
    setParametrosCajaForm((prev) => ({ ...(prev || {}), ticketLayout: layoutParaGuardar, ticketLayoutRegistrado: true }));
  };

  const cargarPropsTicket = async (idCaja) => {
    if (!idCaja) return;
    try {
      const nombreLayout = `TICKET_LAYOUT_${idCaja}`;
      const respProps = await GetRequest({
        url: apiParametrosGet.replace("{nombre}", nombreLayout),
        loader: "Consultando propiedades de ticket...",
      });

      if (!respProps.hasError && respProps.result != null) {
        const parsed = typeof respProps.result === "string" ? JSON.parse(respProps.result) : respProps.result;
        setTicketLayout(parsed || defaultTicketLayout);
        setServerTicketLayout(parsed || defaultTicketLayout);
      } else {
        setTicketLayout(defaultTicketLayout);
        setServerTicketLayout(defaultTicketLayout);
      }
    } catch (e) {
      setTicketLayout(defaultTicketLayout);
      setServerTicketLayout(defaultTicketLayout);
    }
  };

  const aplicarImportacionEsquema = async () => {
    const fromId = getEquipoId(importarDesdeEquipo);
    const toId = getEquipoId(selectedEquipo);

    if (!fromId || !toId) {
      setLocalAlert({ type: "error", message: "Selecciona la caja de origen." });
      return;
    }
    if (fromId === toId) {
      setLocalAlert({ type: "error", message: "No puedes importar el esquema desde la misma caja." });
      return;
    }

    setImportandoEsquema(true);
    try {
      const nombreLayout = `TICKET_LAYOUT_${fromId}`;
      const resp = await GetRequest({
        url: apiParametrosGet.replace("{nombre}", nombreLayout),
        loader: "Consultando esquema de ticket...",
      });

      if (resp?.hasError || resp?.result == null) {
        throw new Error(resp?.message || "La caja seleccionada no tiene un esquema de ticket configurado.");
      }

      const parsed = typeof resp.result === "string" ? JSON.parse(resp.result) : resp.result;
      if (!parsed || typeof parsed !== "object") {
        throw new Error("El esquema de la caja seleccionada no es válido.");
      }

      const importado = {
        ...defaultTicketLayout,
        ...parsed,
        datos: normalizarSeccionTicket(parsed?.datos, CAMPOS_DATOS),
        detalle: normalizarSeccionTicket(parsed?.detalle, CAMPOS_DETALLE),
        totales: normalizarSeccionTicket(parsed?.totales, CAMPOS_TOTALES),
      };

      setTicketLayout(importado);
      setSeleccionObjeto({ seccion: null, id: null });
      setOpenImportarEsquema(false);
      setLocalAlert({
        type: "success",
        message: "Esquema importado. Presiona Guardar para aplicarlo a esta caja (los folios no se modifican).",
      });
    } catch (error) {
      setLocalAlert({ type: "error", message: error?.message || "No fue posible importar el esquema." });
    } finally {
      setImportandoEsquema(false);
    }
  };

  const setField = (name, value) => {
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const onCancel = () => {
    setDraft(server);
    setTicketLayout(serverTicketLayout);
    setLocalAlert(null);
  };

  const onDeshacer = () => {
    setSelectedEquipo(null);
    setServer(null);
    setDraft(null);
    setTicketLayout(defaultTicketLayout);
    setServerTicketLayout(defaultTicketLayout);
    setLocalAlert(null);
    setTab(0);
  };

  const onSave = async () => {
    if (!draft) return;
    setLocalAlert(null);
    setSaving(true);
    try {
      const saved = await handleSaveParametrosCaja(draft);
      if (saved) {
        window.scrollTo(0, 0);
        const norm = normalizeCaja ? normalizeCaja(saved) : saved;
        setServer(norm);
        setDraft(norm);
        setParametrosCajaForm(norm);
        await guardarPropsTicket();
        setLocalAlert({ type: "success", message: "Cambios guardados." });
      }
    } catch (err) {
      setLocalAlert({ type: "error", message: err?.message || "No fue posible guardar." });
    } finally {
      setSaving(false);
    }
  };

  const openCloneModal = () => {
    if (!draft) return;
    setCloneFromEquipo(null);
    setOpenClone(true);
  };

  const applyClone = async () => {
    if (!draft || !cloneFromEquipo) return;

    const fromId = getEquipoId(cloneFromEquipo);
    const toId = getEquipoId(selectedEquipo);

    if (!fromId || !toId) return;
    if (fromId === toId) {
      setLocalAlert({ type: "error", message: "No puedes clonar desde la misma caja." });
      return;
    }

    setCloneLoading(true);
    try {
      const source = await handleObtenerParametrosCaja(fromId);
      if (!source) {
        setLocalAlert({ type: "error", message: "No se pudieron obtener los parámetros de la caja origen." });
        return;
      }

      const src = normalizeCaja ? normalizeCaja(source) : source;

      let next = { ...draft };

      // Clonación selectiva (sin folios, por diseño)
      const cloned = cloneConfigNoFolios(next, src);

      next = { ...next };

      if (cloneKeepGeneral) {
        next.dominio = cloned.dominio;
      }
      if (cloneKeepTicket) {
        next.encabezadoticket = cloned.encabezadoticket;
        next.pieticket = cloned.pieticket;
        next.logo = cloned.logo;

        // Clonar también la nueva construcción del ticket (TICKET_LAYOUT)
        try {
          const nombreLayout = `TICKET_LAYOUT_${fromId}`;
          const respProps = await GetRequest({
            url: apiParametrosGet.replace("{nombre}", nombreLayout),
            loader: "Consultando diseño de ticket...",
          });
          if (!respProps?.hasError && respProps?.result != null) {
            const parsed = typeof respProps.result === "string" ? JSON.parse(respProps.result) : respProps.result;
            if (parsed && typeof parsed === "object") {
              const importado = {
                ...defaultTicketLayout,
                ...parsed,
                datos: normalizarSeccionTicket(parsed?.datos, CAMPOS_DATOS),
                detalle: normalizarSeccionTicket(parsed?.detalle, CAMPOS_DETALLE),
                totales: normalizarSeccionTicket(parsed?.totales, CAMPOS_TOTALES),
              };
              setTicketLayout(importado);
              setSeleccionObjeto({ seccion: null, id: null });
            }
          }
        } catch (e) {
          // Si no hay layout en origen, se conserva el actual
        }
      }
      if (cloneKeepPrinters) {
        next.nombreimpresoraventas = cloned.nombreimpresoraventas;
        next.nombreimpresorapreventas = cloned.nombreimpresorapreventas;
        next.nombreimpresoracotizaciones = cloned.nombreimpresoracotizaciones;
        next.nombreimpresoraapartados = cloned.nombreimpresoraapartados;
        next.nombreimpresorareportes = cloned.nombreimpresorareportes;
        next.nombreimpresorabanner = cloned.nombreimpresorabanner;
        next.nombreimpresoraetiqueta = cloned.nombreimpresoraetiqueta;
        next.nombreimpresoracodigobarras = cloned.nombreimpresoracodigobarras;
      }
      if (cloneKeepFlags) {
        next.statusviewclient = cloned.statusviewclient;
        next.locationviewclient = cloned.locationviewclient;
        next.sizeviewclient = cloned.sizeviewclient;
      }

      next = normalizeCaja ? normalizeCaja(next) : next;
      setDraft(next);
      setOpenClone(false);
      setLocalAlert({ type: "success", message: "Configuración clonada (folios intactos, como Dios manda)." });
      setTab(5);
    } finally {
      setCloneLoading(false);
    }
  };

  const handleChangeDataWhatsappID = (e) => {
    const value = e.target.value ?? "";
    setField("idphonewhatsapp", value);
  }

  const handleChangeDataWhatsappToken = (e) => {
    const value = e.target.value ?? "";
    setField("tokenaccesowhatsapp", value);
  }

  const printerOptions = printersInfo.printers;

  const printerFieldProps = {
    draft,
    options: printerOptions,
    loading: printersLoading,
    listOk: printersInfo.ok,
    setField,
  };

  return (
    <SizeSoftXLayoutForm
        titlePage="Parámetros de Caja"
        buttonActions={
            <MnuParametrosCajaActions 
                handleClickRegresarMainDashboard={handleClickRegresarMainDashboard}
                hasDraft={!!draft}
                isDirty={dirty}
                onSave={onSave}
                onDeshacer={onDeshacer}
            />
        }
    >
    <Box sx={{ width: "100%", flex: 1, minWidth: 0 }}>
        <Card sx={{ width: "100%" }}>
        <CardContent sx={{ width: "100%", minWidth: 0 }}>

            <Divider sx={{ my: 2 }} />

            {localAlert && (
            <Alert severity={localAlert.type} sx={{ mb: 2 }}>
                {localAlert.message}
            </Alert>
            )}

            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <Autocomplete
                    sx={{ width:'300px'}}
                    options={listaEquipos ?? []}
                    value={selectedEquipo}
                    onChange={(_, val) => {
                        setSelectedEquipo(val);
                        if (val) loadCaja(val);
                    }}
                    getOptionLabel={getEquipoLabel}
                    isOptionEqualToValue={(opt, val) => getEquipoId(opt) === getEquipoId(val)}
                    renderInput={(params) => <TextField {...params} label="Selecciona caja/equipo" size="small" />}
                />
            {loadingCaja && (
                <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} />
                <Typography variant="body2">Cargando parámetros…</Typography>
                </Box>
            )}

            {draft && (
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <Chip label={`Caja: ${(draft?.caja ?? "").toString().trim() || getEquipoId(selectedEquipo)}`} />
                {dirty ? <Chip color="warning" label="Cambios pendientes" /> : <Chip color="success" label="Sin cambios" />}
                </Box>
            )}
            </Box>

            {draft && (
            <>
                <Divider sx={{ my: 2 }} />

                {!printersInfo.ok && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {printersInfo.message}. Si quieres autocompletar, levanta tu servicio local (PrinterTools) en
                    localhost:4000.
                </Alert>
                )}

                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                <Tab label="General" />
                <Tab label="Folios" />
                <Tab label="Ticket" />
                <Tab label="Impresoras" />
                <Tab label="WhatsApp" />
                <Tab label="Clonar" />
                </Tabs>

                {dirty && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Tienes cambios sin aplicar. El ticket de prueba usa el diseño de esta pantalla, pero
                        <strong> las ventas reales usarán el diseño guardado</strong> hasta que presiones <strong>Aplicar</strong>.
                    </Alert>
                )}

                <TabPanel value={tab} index={0}>
                <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Caja"
                        size="small"
                        fullWidth
                        value={draft.caja ?? ""}
                        onChange={(e) => setField("caja", e.target.value)}
                    />
                    </Box>
                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Dominio"
                        size="small"
                        fullWidth
                        value={draft.dominio ?? ""}
                        onChange={(e) => setField("dominio", e.target.value)}
                    />
                    </Box>

                    <Box gridColumn="span 4">
                    <FormControlLabel
                        control={
                        <Switch
                            checked={Boolean(draft.statusviewclient ?? false)}
                            onChange={(e) => setField("statusviewclient", e.target.checked)}
                        />
                        }
                        label="Activar ViewClient"
                    />
                    </Box>

                    <Box gridColumn="span 6">
                    <TextField autoComplete="off"
                        label="Location ViewClient"
                        size="small"
                        fullWidth
                        value={draft.locationviewclient ?? ""}
                        onChange={(e) => setField("locationviewclient", e.target.value)}
                    />
                    </Box>

                    <Box gridColumn="span 6">
                    <TextField autoComplete="off"
                        label="Size ViewClient"
                        size="small"
                        fullWidth
                        value={draft.sizeviewclient ?? ""}
                        onChange={(e) => setField("sizeviewclient", e.target.value)}
                    />
                    </Box>
                </Box>
                </TabPanel>

                <TabPanel value={tab} index={1}>
                <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Folio venta"
                        size="small"
                        type="text"
                        inputMode="numeric"
                        enterKeyHint="enter"
                        fullWidth
                        value={draft.folioventa ?? 0}
                        onChange={(e) => setField("folioventa", e.target.value)}
                    />
                    </Box>
                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Folio preventa"
                        size="small"
                        type="text"
                        inputMode="numeric"
                        enterKeyHint="enter"
                        fullWidth
                        value={draft.foliopreventa ?? 0}
                        onChange={(e) => setField("foliopreventa", e.target.value)}
                    />
                    </Box>
                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Folio cotización"
                        size="small"
                        type="text"
                        inputMode="numeric"
                        enterKeyHint="enter"
                        fullWidth
                        value={draft.foliocotizacion ?? 0}
                        onChange={(e) => setField("foliocotizacion", e.target.value)}
                    />
                    </Box>

                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Folio devolución"
                        size="small"
                        type="text"
                        inputMode="numeric"
                        enterKeyHint="enter"
                        fullWidth
                        value={draft.foliodevolucion ?? 0}
                        onChange={(e) => setField("foliodevolucion", e.target.value)}
                    />
                    </Box>
                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Folio apartado"
                        size="small"
                        type="text"
                        inputMode="numeric"
                        enterKeyHint="enter"
                        fullWidth
                        value={draft.folioapartado ?? 0}
                        onChange={(e) => setField("folioapartado", e.target.value)}
                    />
                    </Box>
                    <Box gridColumn="span 4">
                    <TextField autoComplete="off"
                        label="Folio corte caja"
                        size="small"
                        type="text"
                        inputMode="numeric"
                        enterKeyHint="enter"
                        fullWidth
                        value={draft.foliocortecaja ?? 0}
                        onChange={(e) => setField("foliocortecaja", e.target.value)}
                    />
                    </Box>

                    <Box gridColumn="span 12">
                    <Alert severity="warning">
                        Aquí **no hay clonación de folios**. Los folios son únicos: se configuran por caja.
                    </Alert>
                    </Box>
                </Box>
                </TabPanel>

                <TabPanel value={tab} index={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                    <Button variant="outlined" size="small" onClick={handleVistaPreviaTicketPrueba}>
                        Vista previa (datos de prueba)
                    </Button>
                    <Button variant="outlined" size="small" color="primary" disabled={probandoTicket} onClick={handleImprimirTicketPrueba}>
                        {probandoTicket ? "Enviando..." : "Imprimir ticket de prueba"}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!selectedEquipo}
                        onClick={() => {
                            setImportarDesdeEquipo(null);
                            setOpenImportarEsquema(true);
                        }}
                    >
                        Importar esquema de otra caja
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                        La impresión de prueba usa 2 partidas ficticias y no registra ninguna venta.
                    </Typography>
                </Box>
                <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                    <Box gridColumn="span 6">
                        {draft.logo ? (
                            <img src={`data:image/png;base64,${draft.logo}`} alt="Logo" style={{ width: `${(ticketLayout?.logo?.ancho ?? 50) * 3.78}px`, height: (ticketLayout?.logo?.alto ?? 0) > 0 ? `${(ticketLayout?.logo?.alto ?? 0) * 3.78}px` : "auto", border: "1px solid #ccc" }} />
                        ) : (
                            <Box sx={{ p: 1, border: "1px dashed #ccc", borderRadius: 1, color: "#999", fontSize: 13, textAlign: "center" }}>Sin logo</Box>
                        )}
                    </Box>
                    <Box gridColumn="span 6">
                    <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>Logo</Typography>
                        <Box sx={{ mt: 1 }}>
                            <Button variant="contained" component="label" size="small">
                                Subir logo
                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const reader = new FileReader();
                                        reader.onload = (ev) => {
                                            const img = new Image();
                                            img.onload = () => {
                                                const maxW = 384;
                                                const scale = img.width > maxW ? maxW / img.width : 1;
                                                const w = Math.round(img.width * scale);
                                                const h = Math.round(img.height * scale);
                                                const canvas = document.createElement("canvas");
                                                canvas.width = w;
                                                canvas.height = h;
                                                const ctx = canvas.getContext("2d");
                                                ctx.drawImage(img, 0, 0, w, h);
                                                const imageData = ctx.getImageData(0, 0, w, h);
                                                for (let i = 0; i < imageData.data.length; i += 4) {
                                                    const gray = 0.299 * imageData.data[i] + 0.587 * imageData.data[i + 1] + 0.114 * imageData.data[i + 2];
                                                    const bw = gray > 128 ? 255 : 0;
                                                    imageData.data[i] = bw;
                                                    imageData.data[i + 1] = bw;
                                                    imageData.data[i + 2] = bw;
                                                }
                                                ctx.putImageData(imageData, 0, 0);
                                                const dataUrl = canvas.toDataURL("image/png");
                                                setField("logo", dataUrl.split(",")[1]);
                                            };
                                            img.src = ev.target.result;
                                        };
                                        reader.readAsDataURL(file);
                                    }}
                                />
                            </Button>
                        </Box>
                        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1} sx={{ mt: 1 }}>
                            <TextField autoComplete="off" label="Ancho (cm)" size="small" type="number" step="0.1" onFocus={(e) => e.target.select()} value={mmToCm(ticketLayout?.logo?.ancho ?? 50)} onChange={(e) => setTicketLayout((prev) => ({ ...prev, logo: { ...prev.logo, ancho: cmToMm(e.target.value) } }))} />
                            <TextField autoComplete="off" label="Alto (cm)" size="small" type="number" step="0.1" onFocus={(e) => e.target.select()} value={mmToCm(ticketLayout?.logo?.alto ?? 0)} onChange={(e) => setTicketLayout((prev) => ({ ...prev, logo: { ...prev.logo, alto: cmToMm(e.target.value) } }))} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            El pie usa el ancho del ticket (definido en Encabezado).
                        </Typography>
                    </Box>
                    </Box>
                    <Box gridColumn="span 6">
                    <TextField autoComplete="off"
                        label="Encabezado ticket"
                        size="small"
                        multiline
                        minRows={4}
                        sx={{
                            width: `${anchoPapelPx(anchoDotsTicket)}px`,
                            "& .MuiInputBase-root": { height: `${Math.max(40, (ticketLayout?.cabecero?.alto ?? 50) * 3.78)}px`, alignItems: "flex-start" },
                            "& .MuiInputBase-input": {
                                fontFamily: '"Courier New", Courier, monospace',
                                fontWeight: ticketLayout?.cabecero?.negrita ? "bold" : "normal",
                                textAlign: ticketLayout?.cabecero?.alineacion ?? "left",
                                fontSize: `${tamanoFuentePreviewCaracter(calibracionEfectiva.anchoCaracterDots, multiplicadorFuenteTipo(ticketLayout?.cabecero?.fuenteTipo))}px`,
                                height: "100% !important",
                                overflow: "auto",
                            }
                        }}
                        value={draft.encabezadoticket ?? ""}
                        onChange={(e) => setField("encabezadoticket", e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                        }}
                        helperText="Tip: respeta saltos de línea; se imprimen tal cual."
                    />
                    </Box>
                    <Box gridColumn="span 6">
                    <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>Encabezado ticket</Typography>
                        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1} sx={{ mt: 1 }}>
                            <TextField
                                autoComplete="off"
                                label="Ancho del ticket (cm)"
                                size="small"
                                type="number" onFocus={(e) => e.target.select()}
                                value={mmToCm(anchoUtilMmReal)}
                                disabled
                                helperText="Derivado: columnas × ancho del carácter"
                            />
                            <TextField
                                autoComplete="off"
                                label="Columnas por línea"
                                size="small"
                                type="number" onFocus={(e) => e.target.select()}
                                value={ticketLayout?.columnas ?? COLUMNAS_TICKET}
                                onChange={(e) => setTicketLayout((prev) => ({ ...prev, columnas: Math.max(8, Math.min(60, Number(e.target.value) || COLUMNAS_TICKET)) }))}
                                helperText={`${mmToCm(anchoUtilMmReal).toFixed(2)} cm de área imprimible`}
                            />
                            <TextField
                                autoComplete="off"
                                label="Ancho del carácter (dots)"
                                size="small"
                                type="number" onFocus={(e) => e.target.select()}
                                value={ticketLayout?.calibracion?.anchoCaracterDots ?? CALIBRACION_POR_DEFECTO.anchoCaracterDots}
                                onChange={(e) => setTicketLayout((prev) => ({ ...prev, calibracion: { ...(prev.calibracion || {}), anchoCaracterDots: Math.max(4, Math.min(24, Number(e.target.value) || CALIBRACION_POR_DEFECTO.anchoCaracterDots)) } }))}
                                helperText={`${anchoCaracterMmReal.toFixed(2)} mm por carácter (9 en la mayoría de térmicas)`}
                            />
                            <TextField
                                autoComplete="off"
                                label="Dots por mm"
                                size="small"
                                type="number" onFocus={(e) => e.target.select()}
                                value={ticketLayout?.calibracion?.dotsPorMm ?? CALIBRACION_POR_DEFECTO.dotsPorMm}
                                onChange={(e) => setTicketLayout((prev) => ({ ...prev, calibracion: { ...(prev.calibracion || {}), dotsPorMm: Number(e.target.value) || CALIBRACION_POR_DEFECTO.dotsPorMm } }))}
                                helperText="203 dpi = 8.00 · 180 dpi = 7.09"
                            />
                            <TextField
                                autoComplete="off"
                                label="Alto de renglón (ESC 3)"
                                size="small"
                                type="number" onFocus={(e) => e.target.select()}
                                value={ticketLayout?.calibracion?.unidadesLinea ?? CALIBRACION_POR_DEFECTO.unidadesLinea}
                                onChange={(e) => setTicketLayout((prev) => ({ ...prev, calibracion: { ...(prev.calibracion || {}), unidadesLinea: Math.max(1, Math.min(127, Number(e.target.value) || CALIBRACION_POR_DEFECTO.unidadesLinea)) } }))}
                                helperText={`n/180" = ${altoLineaMm(ticketLayout?.calibracion).toFixed(2)} mm`}
                            />
                            <TextField autoComplete="off" label="Alto (cm)" size="small" type="number" step="0.1" onFocus={(e) => e.target.select()} value={mmToCm(ticketLayout?.cabecero?.alto ?? 50)} onChange={(e) => setTicketLayout((prev) => ({ ...prev, cabecero: { ...prev.cabecero, alto: cmToMm(e.target.value) } }))} />
                            <TextField autoComplete="off" select label="Tipo fuente" size="small" value={ticketLayout?.cabecero?.fuenteTipo ?? "A"} onChange={(e) => setTicketLayout((prev) => ({ ...prev, cabecero: { ...prev.cabecero, fuenteTipo: e.target.value } }))}>
                                <MenuItem value="A">Font A (12x24)</MenuItem>
                                <MenuItem value="B">Font B (9x17)</MenuItem>
                                <MenuItem value="D">Font D (24x48)</MenuItem>
                            </TextField>
                            <TextField autoComplete="off" select label="Alineación" size="small" value={ticketLayout?.cabecero?.alineacion ?? "center"} onChange={(e) => setTicketLayout((prev) => ({ ...prev, cabecero: { ...prev.cabecero, alineacion: e.target.value } }))}>
                                <MenuItem value="left">Izquierda</MenuItem>
                                <MenuItem value="center">Centro</MenuItem>
                                <MenuItem value="right">Derecha</MenuItem>
                            </TextField>
                        </Box>
                        <FormControlLabel
                            control={<Switch checked={Boolean(ticketLayout?.cabecero?.negrita)} onChange={(e) => setTicketLayout((prev) => ({ ...prev, cabecero: { ...prev.cabecero, negrita: e.target.checked } }))} />}
                            label="Negrita"
                        />
    
                    </Box>
                    </Box>

                    <Box gridColumn="span 12">
                    <TicketDetalleDesigner
                        titulo="Datos de la venta (objetos)"
                        tituloLienzo="Bloque de datos (centímetros)"
                        campos={CAMPOS_DATOS}
                        labels={LABELS_DATOS}
                        anchoMm={ticketLayout?.cabecero?.ancho ?? ANCHO_TICKET_MM}
                        columnas={ticketLayout?.columnas ?? COLUMNAS_TICKET}
                        calibracion={ticketLayout?.calibracion}
                        objetos={ticketLayout?.datos?.objetos ?? []}
                        onChange={(objetos) => setTicketLayout((prev) => ({ ...prev, datos: { ...(prev?.datos || {}), objetos } }))}
                        seccion="datos"
                        seleccionExterna={seleccionObjeto}
                        onSeleccion={setSeleccionObjeto}
                    />
                    </Box>

                    <Box gridColumn="span 12">
                    <TicketDetalleDesigner
                        campos={CAMPOS_DETALLE}
                        labels={LABELS_DETALLE}
                        anchoMm={ticketLayout?.cabecero?.ancho ?? ANCHO_TICKET_MM}
                        columnas={ticketLayout?.columnas ?? COLUMNAS_TICKET}
                        calibracion={ticketLayout?.calibracion}
                        objetos={ticketLayout?.detalle?.objetos ?? []}
                        onChange={(objetos) => setTicketLayout((prev) => ({ ...prev, detalle: { ...(prev?.detalle || {}), objetos } }))}
                        seccion="detalle"
                        seleccionExterna={seleccionObjeto}
                        onSeleccion={setSeleccionObjeto}
                    />
                    </Box>

                    <Box gridColumn="span 12">
                    <TicketDetalleDesigner
                        titulo="Totales generales (objetos)"
                        tituloLienzo="Bloque de totales (centímetros)"
                        campos={CAMPOS_TOTALES}
                        labels={LABELS_TOTALES}
                        anchoMm={ticketLayout?.cabecero?.ancho ?? ANCHO_TICKET_MM}
                        columnas={ticketLayout?.columnas ?? COLUMNAS_TICKET}
                        calibracion={ticketLayout?.calibracion}
                        objetos={ticketLayout?.totales?.objetos ?? []}
                        onChange={(objetos) => setTicketLayout((prev) => ({ ...prev, totales: { ...(prev?.totales || {}), objetos } }))}
                        seccion="totales"
                        seleccionExterna={seleccionObjeto}
                        onSeleccion={setSeleccionObjeto}
                    />
                    </Box>

                    <Box gridColumn="span 6">
                    <TextField autoComplete="off"
                        label="Pie ticket"
                        size="small"
                        multiline
                        minRows={4}
                        sx={{
                            width: `${anchoPapelPx(anchoDotsTicket)}px`,
                            "& .MuiInputBase-root": { height: `${Math.max(40, (ticketLayout?.pie?.alto ?? 50) * 3.78)}px`, alignItems: "flex-start" },
                            "& .MuiInputBase-input": {
                                fontFamily: '"Courier New", Courier, monospace',
                                fontWeight: ticketLayout?.pie?.negrita ? "bold" : "normal",
                                textAlign: ticketLayout?.pie?.alineacion ?? "left",
                                fontSize: `${tamanoFuentePreviewCaracter(calibracionEfectiva.anchoCaracterDots, multiplicadorFuenteTipo(ticketLayout?.pie?.fuenteTipo))}px`,
                                height: "100% !important",
                                overflow: "auto",
                            }
                        }}
                        value={draft.pieticket ?? ""}
                        onChange={(e) => setField("pieticket", e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                        }}
                    />
                    </Box>
                    <Box gridColumn="span 6">
                    <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>Pie ticket</Typography>
                        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1} sx={{ mt: 1 }}>
                            <TextField autoComplete="off" label="Alto (cm)" size="small" type="number" step="0.1" onFocus={(e) => e.target.select()} value={mmToCm(ticketLayout?.pie?.alto ?? 50)} onChange={(e) => setTicketLayout((prev) => ({ ...prev, pie: { ...prev.pie, alto: cmToMm(e.target.value) } }))} />
                            <TextField autoComplete="off" select label="Tipo fuente" size="small" value={ticketLayout?.pie?.fuenteTipo ?? "A"} onChange={(e) => setTicketLayout((prev) => ({ ...prev, pie: { ...prev.pie, fuenteTipo: e.target.value } }))}>
                                <MenuItem value="A">Font A (12x24)</MenuItem>
                                <MenuItem value="B">Font B (9x17)</MenuItem>
                                <MenuItem value="D">Font D (24x48)</MenuItem>
                            </TextField>
                            <TextField autoComplete="off" select label="Alineación" size="small" value={ticketLayout?.pie?.alineacion ?? "center"} onChange={(e) => setTicketLayout((prev) => ({ ...prev, pie: { ...prev.pie, alineacion: e.target.value } }))}>
                                <MenuItem value="left">Izquierda</MenuItem>
                                <MenuItem value="center">Centro</MenuItem>
                                <MenuItem value="right">Derecha</MenuItem>
                            </TextField>
                        </Box>
                        <FormControlLabel
                            control={<Switch checked={Boolean(ticketLayout?.pie?.negrita)} onChange={(e) => setTicketLayout((prev) => ({ ...prev, pie: { ...prev.pie, negrita: e.target.checked } }))} />}
                            label="Negrita"
                        />

                    </Box>
                    </Box>
                </Box>
                </TabPanel>

                <TabPanel value={tab} index={3}>
                <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora ventas" field="nombreimpresoraventas" {...printerFieldProps} />
                    </Box>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora preventas" field="nombreimpresorapreventas" {...printerFieldProps} />
                    </Box>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora cotizaciones" field="nombreimpresoracotizaciones" {...printerFieldProps} />
                    </Box>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora apartados" field="nombreimpresoraapartados" {...printerFieldProps} />
                    </Box>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora reportes" field="nombreimpresorareportes" {...printerFieldProps} />
                    </Box>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora banner" field="nombreimpresorabanner" {...printerFieldProps} />
                    </Box>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora etiqueta" field="nombreimpresoraetiqueta" {...printerFieldProps} />
                    </Box>
                    <Box gridColumn="span 6">
                    <PrinterField label="Impresora código de barras" field="nombreimpresoracodigobarras" {...printerFieldProps} />
                    </Box>
                </Box>
                </TabPanel>

                <TabPanel value={tab} index={4}>
                <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                    <Box gridColumn="span 3">
                    <TextField autoComplete="off"
                        label="ID de Teléfono (WhatsApp Business)"
                        size="small"
                        fullWidth
                        value={draft.idphonewhatsapp ?? ""}
                        onChange={handleChangeDataWhatsappID}
                        helperText="Identificador numérico del teléfono -- valor corto (ID)"
                    />
                    </Box>
                    <Box gridColumn="span 12">
                    <TextField autoComplete="off"
                        label="Token de Acceso (WhatsApp Business)"
                        size="small"
                        fullWidth
                        value={draft.tokenaccesowhatsapp ?? ""}
                        onChange={handleChangeDataWhatsappToken}
                        helperText="Token de acceso de la API -- valor largo alfanumérico (Token)"
                    />
                    </Box>
                </Box>
                </TabPanel>

                <TabPanel value={tab} index={5}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                    <Typography variant="subtitle1">Clonar configuración desde otra caja</Typography>
                    <Button variant="outlined" onClick={openCloneModal}>
                    Abrir clonación
                    </Button>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                    Clona lo útil (impresoras/ticket/flags/dominio) y **deja folios intactos**.
                </Alert>
                </TabPanel>
            </>
            )}

            {/* MODAL CLONAR */}
            <Dialog open={openClone} onClose={() => setOpenClone(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Clonar configuración</DialogTitle>
            <DialogContent dividers>
                <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                <Box gridColumn="span 12">
                    <Autocomplete
                    options={(listaEquipos ?? []).filter((x) => getEquipoId(x) !== getEquipoId(selectedEquipo))}
                    value={cloneFromEquipo}
                    onChange={(_, val) => setCloneFromEquipo(val)}
                    getOptionLabel={getEquipoLabel}
                    renderInput={(params) => (
                        <TextField {...params} label="Caja origen" size="small" />
                    )}
                    />
                </Box>

                <Box gridColumn="span 12">
                    <FormControlLabel
                    control={<Switch checked={cloneKeepGeneral} onChange={(e) => setCloneKeepGeneral(e.target.checked)} />}
                    label="Clonar General (dominio)"
                    />
                </Box>
                <Box gridColumn="span 12">
                    <FormControlLabel
                    control={<Switch checked={cloneKeepTicket} onChange={(e) => setCloneKeepTicket(e.target.checked)} />}
                    label="Clonar Ticket (encabezado/pie)"
                    />
                </Box>
                <Box gridColumn="span 12">
                    <FormControlLabel
                    control={
                        <Switch checked={cloneKeepPrinters} onChange={(e) => setCloneKeepPrinters(e.target.checked)} />
                    }
                    label="Clonar Impresoras"
                    />
                </Box>
                <Box gridColumn="span 12">
                    <FormControlLabel
                    control={<Switch checked={cloneKeepFlags} onChange={(e) => setCloneKeepFlags(e.target.checked)} />}
                    label="Clonar Flags (ViewClient)"
                    />
                </Box>

                <Box gridColumn="span 12">
                    <Alert severity="warning">
                    No se clonan folios. Si alguien te dice lo contrario, te quiere ver arder en producción.
                    </Alert>
                </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenClone(false)} disabled={cloneLoading}>
                Cancelar
                </Button>
                <Button
                variant="contained"
                onClick={applyClone}
                disabled={!cloneFromEquipo || cloneLoading}
                >
                {cloneLoading ? "Clonando..." : "Aplicar clonación"}
                </Button>
            </DialogActions>
            </Dialog>
            <Dialog open={openImportarEsquema} onClose={() => setOpenImportarEsquema(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Importar esquema de ticket</DialogTitle>
                <DialogContent dividers>
                    <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                        <Box gridColumn="span 12">
                            <Autocomplete
                                options={(listaEquipos ?? []).filter((x) => getEquipoId(x) !== getEquipoId(selectedEquipo))}
                                value={importarDesdeEquipo}
                                onChange={(_, val) => setImportarDesdeEquipo(val)}
                                getOptionLabel={getEquipoLabel}
                                renderInput={(params) => (
                                    <TextField {...params} label="Caja origen" size="small" />
                                )}
                            />
                        </Box>
                        <Box gridColumn="span 12">
                            <Alert severity="info">
                                Se copia únicamente el diseño del ticket (cabecero, datos, detalle, totales y pie).
                                No se copian folios, logo, impresoras ni configuración general.
                            </Alert>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenImportarEsquema(false)} disabled={importandoEsquema}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={aplicarImportacionEsquema}
                        disabled={!importarDesdeEquipo || importandoEsquema}
                    >
                        {importandoEsquema ? "Importando..." : "Importar esquema"}
                    </Button>
                </DialogActions>
            </Dialog>
            <TicketPreview
                abierto={Boolean(previewTicket)}
                titulo={previewTicket?.titulo}
                lineas={previewTicket?.lineas || []}
                columnas={columnasEfectivas}
                calibracion={calibracionEfectiva}
                onCerrar={() => setPreviewTicket(null)}
            />
        </CardContent>
        </Card>
    </Box>
    </SizeSoftXLayoutForm>
  );
}


